import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useGetUser from '../hooks/useGetUser';
import useEditUser from '../hooks/useEditUser';
import useGetLists from '@/features/List/hooks/useGetLists';
import useCreateList from '@/features/List/hooks/useCreateList';
import { Save } from 'lucide-react';

type UserFormProps = {
	onSave?: () => void;
};

export default function UserForm({ onSave }: UserFormProps) {
	const { t } = useTranslation();

	const { user } = useGetUser();
	const { lists } = useGetLists();

	const { editUser } = useEditUser();
	const { createList } = useCreateList();

	const isiOSWebview = /\(ip.*applewebkit(?!.*(version|crios))/i.test(
		navigator.userAgent
	);

	const userFormSchema = z.object({
		firstname: isiOSWebview
			? z.string()
			: z.string().min(1, { message: t('register.error.required') }),
		lastname: isiOSWebview
			? z.string()
			: z.string().min(1, { message: t('register.error.required') }),
		birthday: z.string().date(t('user.form.birthday.error')),
	});

	const form = useForm<z.infer<typeof userFormSchema>>({
		resolver: zodResolver(userFormSchema),
		defaultValues: {
			firstname: user?.firstname || '',
			lastname: user?.lastname || '',
			birthday: user?.birthday || '',
		},
	});

	const onSubmit = async (values: z.infer<typeof userFormSchema>) => {
		if (values.firstname === '') values.firstname = 'John';
		if (values.lastname === '') values.lastname = 'Doe';
		editUser({ ...values, image: null });
		if (lists.length === 0) createList({ name: t('list.firstList') });
		setTimeout(() => onSave && onSave(), 100);
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
				<div className="grid grid-cols-2 gap-4">
					<div className="grid gap-2">
						<FormField
							control={form.control}
							name="firstname"
							render={({ field }) => (
								<FormItem>
									<Input
										id="firstname"
										label={t(
											'register.input.firstname.label'
										)}
										autoComplete="given-name"
										placeholder={t(
											'register.input.firstname.placeholder'
										)}
										{...field}
									/>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="grid gap-2">
						<FormField
							control={form.control}
							name="lastname"
							render={({ field }) => (
								<FormItem>
									<Input
										id="lastname"
										label={t(
											'register.input.lastname.label'
										)}
										autoComplete="family-name"
										placeholder={t(
											'register.input.lastname.placeholder'
										)}
										{...field}
									/>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>
				<div className="grid gap-2">
					<FormField
						control={form.control}
						name="birthday"
						render={({ field }) => (
							<FormItem>
								<Input
									id="birthday"
									type="date"
									label={t('user.form.birthday.label')}
									{...field}
								/>

								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<Button type="submit" className="w-fit justify-self-end">
					<Save className="mr-2 h-4 w-4" />
					{t('user.form.button')}
				</Button>
			</form>
		</Form>
	);
}
